/**
 * Phase 19 — Pure Financial Calculations & Treasury Math Precision
 * Safe currency arithmetic, balance calculations, overpayment prevention,
 * overdue analytics, and financial data masking.
 */

import { PaymentStatus, PaymentType } from '@/types/domain/payment-management';

/**
 * Rounds any financial amount to 2 decimal places safely avoiding floating point artifacts.
 */
export function roundCurrency(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Evaluates payment amount against outstanding invoice balance and enforces overpayment prevention.
 */
export function calculateRemainingBalance(
  invoicePayableAmount: number,
  alreadyPaidAmount: number,
  currentPaymentAmount: number
): {
  isValid: boolean;
  newPaidAmount: number;
  remainingAmount: number;
  isOverpayment: boolean;
  overpaymentAmount: number;
} {
  const safeInvoiceTotal = Math.max(0, roundCurrency(invoicePayableAmount));
  const safeAlreadyPaid = Math.max(0, roundCurrency(alreadyPaidAmount));
  const safePayment = Math.max(0, roundCurrency(currentPaymentAmount));

  const remainingBefore = Math.max(0, roundCurrency(safeInvoiceTotal - safeAlreadyPaid));

  if (safePayment > remainingBefore + 0.01) {
    const overpaymentAmount = roundCurrency(safePayment - remainingBefore);
    return {
      isValid: false,
      newPaidAmount: safeAlreadyPaid,
      remainingAmount: remainingBefore,
      isOverpayment: true,
      overpaymentAmount,
    };
  }

  const newPaidAmount = roundCurrency(safeAlreadyPaid + safePayment);
  const remainingAmount = Math.max(0, roundCurrency(safeInvoiceTotal - newPaidAmount));

  return {
    isValid: true,
    newPaidAmount,
    remainingAmount,
    isOverpayment: false,
    overpaymentAmount: 0,
  };
}

/**
 * Resolves whether a disbursement represents a partial payment or full settlement.
 */
export function determinePaymentType(
  amount: number,
  remainingPayableBefore: number
): PaymentType {
  const safeAmount = roundCurrency(amount);
  const safeRemaining = roundCurrency(remainingPayableBefore);

  if (safeAmount < safeRemaining - 0.01) {
    return 'PARTIAL';
  }
  return 'FULL';
}

/**
 * Validates a proposed payment amount against financial rules.
 */
export function validatePaymentAmount(
  amount: number,
  remainingPayableBefore: number
): { isValid: boolean; errorMessage?: string } {
  if (amount === undefined || amount === null || isNaN(amount)) {
    return { isValid: false, errorMessage: 'Payment amount must be a valid number.' };
  }

  if (!isFinite(amount)) {
    return { isValid: false, errorMessage: 'Payment amount cannot be infinite.' };
  }

  if (amount <= 0) {
    return { isValid: false, errorMessage: 'Payment amount must be greater than zero.' };
  }

  if (amount > remainingPayableBefore + 0.01) {
    const diff = roundCurrency(amount - remainingPayableBefore);
    return {
      isValid: false,
      errorMessage: `Payment amount exceeds remaining payable balance by ${diff}. Overpayments are blocked.`,
    };
  }

  return { isValid: true };
}

/**
 * Computes calendar overdue metrics based on target due date, status, and remaining balance.
 */
export function calculateDaysOverdue(
  dueDate: string,
  paymentStatus: PaymentStatus,
  remainingAmount: number = 1
): { isOverdue: boolean; daysOverdue: number } {
  if (
    paymentStatus === 'PAID' ||
    paymentStatus === 'CANCELLED' ||
    remainingAmount <= 0.01 ||
    !dueDate
  ) {
    return { isOverdue: false, daysOverdue: 0 };
  }

  const targetTime = new Date(dueDate).getTime();
  const now = Date.now();
  const diffMs = now - targetTime;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays > 0) {
    return { isOverdue: true, daysOverdue: diffDays };
  }

  return { isOverdue: false, daysOverdue: 0 };
}

/**
 * Securely masks a bank account number for presentation (e.g. •••• •••• 4582).
 */
export function maskAccountNumber(accountNumber: string): string {
  if (!accountNumber) return '•••• •••• ••••';
  const clean = accountNumber.replace(/\s+/g, '');
  if (clean.length <= 4) return clean;
  const lastFour = clean.slice(-4);
  return `•••• •••• ${lastFour}`;
}
